import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { showNotification } from "../../store/slices/notificationSlice";
import { HubError } from "../../class/Error";
import { AccountDetailInfo, MarketEdit } from "../../_app/types";
import { useDispatch } from "react-redux";
import Header from "../../_modules/Header/Header";
import PageLayout from "../../_components/PageLayout";
import { TextFieldStyled } from "../../_components/TextFieldStyled";
import { useTheme } from "@mui/material";
import { updateAccount } from "../../store/slices/userSlice";
import { useAppNavigate } from "../../_hooks/use-app-navigate";
import Spacer from "../../_components/Spacer";
import { useApi } from "../../_hooks/use-api";
import DisconnectAccountPopup from "./components/DisconnectAccountPopup";
import { useTranslation } from "react-i18next";
import MyButton from "../../_components/MyButton";
import MyPaper from "../../_components/MyPaper";
import Loading from "../../_components/Loading";

const AccountSettingsPage = () => {
  const params = useParams<{ accountId: string }>();
  const theme = useTheme();
  const api = useApi();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { t } = useTranslation("account");

  const [account, setAccount] = useState<AccountDetailInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [marketConnect, setMarketConnect] = useState<MarketEdit | null>(null);

  const [isLoadingSaveButton, setIsLoadingSaveButton] = useState<boolean>(false);

  const [isOpenDisconnectModal, setIsOpenDisconnectModal] = useState<boolean>(false);

  const getAccount = async () => {
    setIsLoading(true);

    try {
      const response = await api.getAccountInformation(params.accountId!);
      setAccount(response);
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsLoading(false);
    }
  };

  const saveAccountSettings = async () => {
    if (!account || !marketConnect) return;

    setIsLoadingSaveButton(true);

    try {
      await api.changeAccountSettings(account.id, marketConnect);

      dispatch(updateAccount({ accountId: account.id, accountData: marketConnect }));
      dispatch(
        showNotification({
          message: `Account ${account.name} was renamed`,
          severity: "info",
        })
      );

      navigate.back();
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsLoadingSaveButton(false);
    }
  };

  useEffect(() => {
    getAccount();
  }, []);

  useEffect(() => {
    if (!account) return;

    setMarketConnect({
      name: account.name,
      leverage: 1,
    });
  }, [account]);

  const onChangeAccountName = (value: string) => {
    if (!marketConnect) return;

    setMarketConnect((prevState) => {
      if (prevState) {
        return { ...prevState, name: value };
      }

      return null;
    });
  };

  const onClickDisconnectButton = () => setIsOpenDisconnectModal(true);

  return (
    <PageLayout
      contentSx={{ padding: "20px 8px 0" }}
      header={<Header pageTitle={t("accountSettings")} needBack />}
      bottomButton={
        account &&
        marketConnect && (
          <MyPaper>
            <MyButton onClick={onClickDisconnectButton} variant="danger" sx={{ mb: "8px" }}>
              {t("disconnectAccount")}
            </MyButton>

            <MyButton
              disabled={account.name === marketConnect.name || marketConnect.name === ""}
              onClick={saveAccountSettings}
              variant="primary"
              isLoading={isLoadingSaveButton}
            >
              {t("save")}
            </MyButton>
          </MyPaper>
        )
      }
    >
      {isLoading ? (
        <Loading />
      ) : (
        account &&
        marketConnect && (
          <>
            <MyPaper bgcolor={theme.myTheme.colors.gray400}>
              <TextFieldStyled
                fullWidth={true}
                label={t("accountName")}
                variant="outlined"
                size="small"
                type={"text"}
                value={marketConnect.name}
                onChange={(e) => onChangeAccountName(e.target.value)}
              />
            </MyPaper>

            <Spacer height="145px" />

            <DisconnectAccountPopup
              open={isOpenDisconnectModal}
              onClose={setIsOpenDisconnectModal}
              account={account}
            />
          </>
        )
      )}
    </PageLayout>
  );
};

export default AccountSettingsPage;
